import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Calendar,
  TrendingUp,
  ArrowRight,
  Star,
  Play,
  Award,
  User,
  Building,
  MapPin,
  Briefcase,
  Trophy,
  Lightbulb,
  Sparkles,
  FileText,
} from 'lucide-react';
import { HomePageContent, HeroCarouselSlideItem } from '@/constants/homePageDefaults';

interface HeroCarouselTrackProps {
  order: HeroCarouselSlideItem[];
  homePage: HomePageContent;
  carouselHackathons: any[];
  carouselEvents: any[];
  mentors: any[];
  hackathonFlyerUrls: Record<string, string>;
  eventFlyerUrls: Record<string, string>;
  mentorImageUrls: Record<string, string>;
  setMentorImageUrls: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  isAuthenticated: boolean;
  user: any;
  getDashboardLink: () => string;
  getProfileLink: () => string;
}

function scrollToSectionId(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function renderCtaLink(
  href: string,
  className: string,
  children: React.ReactNode
) {
  // In-page anchor on home: #resume-ai-analysis
  if (href.startsWith('#')) {
    const id = href.slice(1);
    return (
      <a
        href={href}
        className={className}
        onClick={(e) => {
          e.preventDefault();
          scrollToSectionId(id);
        }}
      >
        {children}
      </a>
    );
  }

  // Home page section from any route: /#resume-ai-analysis
  if (href.startsWith('/#')) {
    const id = href.slice(2);
    return (
      <Link
        to={{ pathname: '/', hash: id }}
        className={className}
        onClick={() => {
          if (window.location.pathname === '/') {
            setTimeout(() => scrollToSectionId(id), 50);
          }
        }}
      >
        {children}
      </Link>
    );
  }

  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

/** Wrap [[like this]] in title with brand color. */
function renderCarouselTitle(title: string) {
  const parts = title.split(/(\[\[[^\]]+\]\])/g);
  return parts.map((part, index) => {
    const match = part.match(/^\[\[(.+)\]\]$/);
    if (match) {
      return (
        <span key={index} className="text-primary">
          {match[1]}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

export default function HeroCarouselTrack({
  order,
  homePage,
  carouselHackathons,
  carouselEvents,
  mentors,
  hackathonFlyerUrls,
  eventFlyerUrls,
  mentorImageUrls,
  setMentorImageUrls,
  isAuthenticated,
  user,
  getDashboardLink,
  getProfileLink,
}: HeroCarouselTrackProps) {
  const slides = order.flatMap((item) => {
    if (!item.enabled) return [];

    switch (item.type) {
      case 'hackathons':
        if (carouselHackathons.length === 0) return [];
        return [
          <div key={item.id} className="w-full flex-shrink-0">
            <div className="text-center space-y-6 py-8">
              {/* Hackathon Display */}
              <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-white mt-12">
                <div className="relative h-96 md:h-[500px]">
                  {carouselHackathons.map((hackathon) => (
                    <div key={hackathon._id} className="flex flex-col md:flex-row h-full">
                      {/* Hackathon Flyer */}
                      <div className="w-full md:w-1/2 h-48 md:h-full relative">
                        <img
                          src={hackathonFlyerUrls[hackathon._id] || hackathon.hackathonFlyer}
                          alt={hackathon.title}
                          className="w-full h-full object-contain bg-gray-100"
                          onLoad={() => console.log(`✅ Hackathon flyer image loaded successfully for ${hackathon.title}`)}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzllYTNhOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkhhY2thdGhvbiBGbHllcjwvdGV4dD48L3N2Zz4=';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:hidden" />
                      </div>

                      {/* Hackathon Details */}
                      <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center relative">
                        <div className="space-y-6">
                          <div className="space-y-2">
                            <div className="inline-flex items-center space-x-2 text-primary font-medium text-sm">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {new Date(hackathon.startDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </span>
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold text-secondary line-clamp-2">
                              {hackathon.title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                              {hackathon.description}
                            </p>
                          </div>

                          <div className="space-y-4">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center space-x-2 text-gray-600">
                                <Users className="w-4 h-4" />
                                <span className="text-sm">{hackathon.maxTeams} teams max</span>
                              </div>
                              <div className="flex items-center space-x-2 text-gray-600">
                                <Trophy className="w-4 h-4" />
                                <span className="text-sm">{hackathon.prizes?.length || 0} prizes</span>
                              </div>
                            </div>

                            <Link
                              to={`/events/${hackathon.associatedEventId || hackathon._id}`}
                              className="inline-flex items-center bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                            >
                              <span>{homePage.carouselLabels.learnMore}</span>
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call to Action */}
              <div className="text-center mt-8">
                <Link
                  to="/hackathons"
                  className="inline-flex items-center border-2 border-primary text-primary px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <span>{homePage.carouselLabels.viewAllHackathons}</span>
                  <Trophy className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>
          </div>,
        ];

      case 'events':
        return carouselEvents.map((event, eventIndex) => (
          <div key={event._id} className="w-full flex-shrink-0">
            <div className="text-center space-y-6 py-8">
              {/* Single Event Display */}
              <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-white mt-12">
                <div className="relative h-96 md:h-[500px]">
                  <div className="flex flex-col md:flex-row h-full">
                    {/* Event Flyer */}
                    <div className="w-full md:w-1/2 h-48 md:h-full relative">
                      <img
                        src={eventFlyerUrls[event._id] || event.eventFlyer}
                        alt={event.title}
                        className="w-full h-full object-contain bg-gray-100"
                        onLoad={() => console.log(`✅ Event flyer image loaded successfully for ${event.title}`)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzllYTNhOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkV2ZW50IEZseWVyPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:hidden" />
                    </div>

                    {/* Event Details */}
                    <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center relative">
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <div className="inline-flex items-center space-x-2 text-primary font-medium text-sm">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {new Date(event.startTime).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </span>
                          </div>
                          <h3 className="text-2xl md:text-3xl font-bold text-secondary line-clamp-2">
                            {event.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            {event.description}
                          </p>
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2 text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span className="text-sm">{event.location}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-600">
                              <Users className="w-4 h-4" />
                              <span className="text-sm">{event.capacity} seats</span>
                            </div>
                          </div>

                          <Link
                            to={`/events/${event._id}`}
                            className="inline-flex items-center bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                          >
                            <span>{homePage.carouselLabels.learnMore}</span>
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call to Action for last event slide */}
              {eventIndex === carouselEvents.length - 1 && (
                <div className="text-center mt-8">
                  <Link
                    to="/events"
                    className="inline-flex items-center border-2 border-primary text-primary px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary hover:text-white transition-all duration-300"
                  >
                    <span>{homePage.carouselLabels.viewAllEvents}</span>
                    <Calendar className="w-5 h-5 ml-2" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        ));

      case 'empowering_careers':
        return [
          <div key={item.id} className="w-full flex-shrink-0">
            <div className="text-center space-y-6 py-8">
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-secondary leading-tight">
                  {homePage.heroSlide.titleLine1}
                  <span className="block text-primary">{homePage.heroSlide.titleLine2}</span>
                </h1>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  {homePage.heroSlide.subtitle}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
                {isAuthenticated ? (
                  <>
                    <Link
                      to={getDashboardLink()}
                      className="group bg-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2"
                    >
                      <User className="w-5 h-5" />
                      <span>Go to Dashboard</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                      to={getProfileLink()}
                      className="border-2 border-border text-secondary px-8 py-4 rounded-xl font-semibold text-lg hover:border-primary hover:text-primary transition-all duration-300 flex items-center space-x-2"
                    >
                      <span>View Profile</span>
                      <Play className="w-4 h-4" />
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="group bg-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2"
                    >
                      <span>Get Started</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                      to="/login"
                      className="border-2 border-border text-secondary px-8 py-4 rounded-xl font-semibold text-lg hover:border-primary hover:text-primary transition-all duration-300 flex items-center space-x-2"
                    >
                      <span>Sign In</span>
                      <Play className="w-4 h-4" />
                    </Link>
                  </>
                )}
              </div>

              {/* Welcome message for authenticated users */}
              {isAuthenticated && (
                <div className="pt-8">
                  <div className="bg-surface backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-border">
                    <h3 className="text-2xl font-bold text-secondary mb-2">
                      Welcome back, {user?.firstName || user?.email}!
                    </h3>
                    <p className="text-gray-600">
                      You're logged in as a <span className="font-semibold text-primary capitalize">{user?.role}</span>.
                      Ready to continue your career journey?
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>,
        ];

      case 'success_stories':
        return [
          <div key={item.id} className="w-full flex-shrink-0">
            <div className="text-center space-y-5 py-6">
              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-3">
                  <Trophy className="w-14 h-14 text-primary" />
                  <h1 className="text-4xl sm:text-5xl font-bold text-secondary leading-tight text-center whitespace-nowrap">
                    {homePage.successStoriesSlide.title}
                  </h1>
                </div>
                <div className="max-w-3xl mx-auto">
                  <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-5 mb-4">
                    <div className="text-6xl font-bold text-primary mb-1">{homePage.successStoriesSlide.count}</div>
                    <h2 className="text-2xl md:text-3xl font-bold text-secondary mb-2">
                      {homePage.successStoriesSlide.headline}
                    </h2>
                    <p className="text-lg text-gray-600">
                      {homePage.successStoriesSlide.salaryPrefix}{' '}
                      <span className="font-bold text-primary">{homePage.successStoriesSlide.salaryRangeText}</span>{' '}
                      {homePage.successStoriesSlide.salarySuffix}
                    </p>
                  </div>
                  <p className="text-lg text-gray-600 leading-relaxed mb-5">
                    {homePage.successStoriesSlide.description}
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {homePage.successStoriesSlide.stats.map((stat, index) => {
                      const StatIcons = [TrendingUp, Award, Users];
                      const StatIcon = StatIcons[index] || TrendingUp;
                      return (
                        <div key={index} className="bg-white rounded-xl p-4 shadow-lg">
                          <StatIcon className="w-7 h-7 text-primary mx-auto mb-2" />
                          <div className="text-2xl font-bold text-secondary">{stat.value}</div>
                          <div className="text-sm text-gray-600">{stat.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-5">
                <Link
                  to="/register"
                  className="group bg-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <span>{homePage.successStoriesSlide.primaryCta}</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/jobs"
                  className="border-2 border-border text-secondary px-8 py-4 rounded-xl font-semibold text-lg hover:border-primary hover:text-primary transition-all duration-300 flex items-center space-x-2"
                >
                  <span>{homePage.successStoriesSlide.secondaryCta}</span>
                  <Briefcase className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>,
        ];

      case 'expert_mentors':
        return [
          <div key={item.id} className="w-full flex-shrink-0">
            <div className="text-center space-y-6 py-8">
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-4">
                  <Lightbulb className="w-16 h-16 text-primary animate-pulse" />
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-secondary leading-tight text-center whitespace-nowrap">
                    {homePage.mentorsSlide.title}
                  </h1>
                </div>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  {homePage.mentorsSlide.subtitle}
                </p>
              </div>

              {/* Mentors Grid */}
              {mentors.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                  {mentors.slice(0, 4).map((mentor) => (
                    <div key={mentor._id} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4 relative">
                        {mentorImageUrls[mentor._id] ? (
                          <img
                            src={mentorImageUrls[mentor._id]}
                            alt={`${mentor.firstName} ${mentor.lastName}`}
                            className="w-20 h-20 rounded-full object-cover"
                            onLoad={() => console.log('✅ Mentor image loaded successfully:', mentor.firstName)}
                            onError={() => {
                              console.error('❌ Mentor image failed to load:', mentor.firstName);
                              setMentorImageUrls((prev) => {
                                const updated = { ...prev };
                                delete updated[mentor._id];
                                return updated;
                              });
                            }}
                          />
                        ) : null}
                        <User
                          className={`w-10 h-10 text-white ${mentorImageUrls[mentor._id] ? 'hidden' : 'block'}`}
                        />
                      </div>
                      <h3 className="text-lg font-bold text-secondary mb-1">
                        {mentor.firstName} {mentor.lastName}
                      </h3>
                      <div className="text-sm text-primary font-medium mb-2">
                        {mentor.professionalInfo?.currentPosition}
                      </div>
                      <div className="flex items-center justify-center text-xs text-gray-600 mb-3">
                        <Building className="w-3 h-3 mr-1" />
                        {mentor.professionalInfo?.currentCompany}
                      </div>
                      <div className="text-xs text-gray-500">
                        {mentor.professionalInfo?.yearsOfExperience} years experience
                      </div>
                      <div className="mt-3 flex flex-wrap justify-center gap-1">
                        {mentor.primarySkills?.slice(0, 2).map((skill: string, index: number) => (
                          <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">{homePage.mentorsSlide.emptyTitle}</p>
                  <p className="text-gray-500 text-sm">{homePage.mentorsSlide.emptySubtitle}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
                <Link
                  to="/mentors"
                  className="group bg-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <span>{homePage.mentorsSlide.primaryCta}</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/register"
                  className="border-2 border-border text-secondary px-8 py-4 rounded-xl font-semibold text-lg hover:border-primary hover:text-primary transition-all duration-300 flex items-center space-x-2"
                >
                  <span>{homePage.mentorsSlide.secondaryCta}</span>
                  <Users className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>,
        ];

      case 'custom': {
        const custom = item.custom;
        if (!custom?.title?.trim()) return [];
        const isFeatured = custom.variant === 'featured';
        return [
          <div key={item.id} className="w-full flex-shrink-0">
            <div className="text-center space-y-6 py-8">
              {isFeatured ? (
                <div className="relative max-w-4xl mx-auto rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50/90 via-white to-primary/5 px-6 py-10 sm:px-10 shadow-lg">
                  {custom.badge?.trim() && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
                      <Sparkles className="w-4 h-4" />
                      {custom.badge}
                    </div>
                  )}
                  <div className="space-y-4">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-secondary leading-tight">
                      {renderCarouselTitle(custom.title)}
                    </h1>
                    {custom.subtitle && (
                      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-5 max-w-2xl mx-auto">
                        <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed">
                          {custom.subtitle}
                        </p>
                      </div>
                    )}
                  </div>
                  {(custom.primaryCta || custom.secondaryCta) && (
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
                      {custom.primaryCta && custom.primaryCtaLink &&
                        renderCtaLink(
                          custom.primaryCtaLink,
                          'group bg-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2',
                          <>
                            <span>{custom.primaryCta}</span>
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      {custom.secondaryCta && custom.secondaryCtaLink &&
                        renderCtaLink(
                          custom.secondaryCtaLink,
                          'border-2 border-border text-secondary px-8 py-4 rounded-xl font-semibold text-lg hover:border-primary hover:text-primary transition-all duration-300 flex items-center space-x-2',
                          <>
                            <span>{custom.secondaryCta}</span>
                            <FileText className="w-4 h-4" />
                          </>
                        )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-secondary leading-tight">
                      {renderCarouselTitle(custom.title)}
                    </h1>
                    {custom.subtitle && (
                      <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        {custom.subtitle}
                      </p>
                    )}
                  </div>

                  {(custom.primaryCta || custom.secondaryCta) && (
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
                      {custom.primaryCta && custom.primaryCtaLink &&
                        renderCtaLink(
                          custom.primaryCtaLink,
                          'group bg-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-primary-dark transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2',
                          <>
                            <span>{custom.primaryCta}</span>
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      {custom.secondaryCta && custom.secondaryCtaLink &&
                        renderCtaLink(
                          custom.secondaryCtaLink,
                          'border-2 border-border text-secondary px-8 py-4 rounded-xl font-semibold text-lg hover:border-primary hover:text-primary transition-all duration-300 flex items-center space-x-2',
                          <>
                            <span>{custom.secondaryCta}</span>
                            <Star className="w-4 h-4" />
                          </>
                        )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>,
        ];
      }

      default:
        return [];
    }
  });

  return <>{slides}</>;
}
